import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Send,
  Download,
  Receipt as ReceiptIcon,
  Loader2,
  MessageCircle,
} from "lucide-react";
import {
  buildReceiptCanvas,
  canvasToPngBlob,
  buildWhatsAppText,
} from "@/lib/receipt";

const STORE_WHATSAPP = "5566999262904"; // 55 + DDD + número (MIDIA STORE)

function formatBRL(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateTime(iso) {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ReceiptCard({ payment }) {
  const [busy, setBusy] = useState(false);

  const data = useMemo(
    () => ({
      amount: formatBRL(payment.amount),
      txId: payment.transaction_id,
      dateStr: formatDateTime(payment.created_at),
    }),
    [payment]
  );

  const generateBlob = async () => {
    const canvas = buildReceiptCanvas(data);
    return await canvasToPngBlob(canvas);
  };

  const downloadPng = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comprovante-${data.txId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent(buildWhatsAppText(data));
    const url = `https://wa.me/${STORE_WHATSAPP}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const blob = await generateBlob();
      const file = new File([blob], `comprovante-${data.txId}.png`, {
        type: "image/png",
      });

      // Try native Web Share API with files
      const canShareFile =
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] });

      if (canShareFile && navigator.share) {
        try {
          await navigator.share({
            files: [file],
            title: "Comprovante PIX - MÍDIA STORE",
            text: buildWhatsAppText(data),
          });
          toast.success("Pronto! Selecione o WhatsApp para enviar.");
          return;
        } catch (err) {
          if (err && err.name === "AbortError") {
            return; // user cancelled the share sheet
          }
          // fall through to fallback
        }
      }

      // Fallback: download PNG + open WhatsApp link
      downloadPng(blob);
      toast.success("Comprovante baixado. Anexe no WhatsApp.", {
        description: "Abrindo o WhatsApp com a mensagem pré-preenchida...",
      });
      setTimeout(openWhatsApp, 600);
    } catch (e) {
      toast.error("Não foi possível gerar o comprovante. Tente novamente.");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadOnly = async () => {
    setBusy(true);
    try {
      const blob = await generateBlob();
      downloadPng(blob);
      toast.success("Comprovante baixado!");
    } catch (e) {
      toast.error("Falha ao gerar comprovante");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-7 rounded-2xl p-5 sm:p-6 pop"
      style={{
        background:
          "linear-gradient(180deg, rgba(0,255,136,0.08), rgba(0,255,136,0.02))",
        border: "1px solid rgba(0,255,136,0.35)",
        boxShadow: "0 0 28px rgba(0,255,136,0.18)",
      }}
      data-testid="receipt-card"
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: "rgba(0,255,136,0.15)",
            border: "1px solid rgba(0,255,136,0.55)",
          }}
        >
          <ReceiptIcon className="w-5 h-5 text-[var(--neon)]" />
        </div>
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-[var(--neon)] tracking-wide">
            Envie o comprovante PIX via WhatsApp
          </h3>
          <p className="text-xs sm:text-sm text-[var(--muted)] mt-1 leading-relaxed">
            Seu pagamento foi confirmado. Compartilhe o comprovante com a loja
            para liberar seu pedido.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-5">
        <button
          type="button"
          onClick={handleShare}
          disabled={busy}
          className="neon-btn rounded-xl py-3.5 px-4 mono uppercase tracking-[0.16em] text-xs flex items-center justify-center gap-2"
          data-testid="share-whatsapp-btn"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 spin" /> Gerando...
            </>
          ) : (
            <>
              <MessageCircle className="w-4 h-4" /> Enviar no WhatsApp
            </>
          )}
        </button>

        <button
          type="button"
          onClick={handleDownloadOnly}
          disabled={busy}
          className="neon-ghost-btn rounded-xl py-3.5 px-4 mono uppercase tracking-[0.16em] text-xs flex items-center justify-center gap-2"
          data-testid="download-receipt-btn"
        >
          <Download className="w-4 h-4" /> Baixar comprovante
        </button>
      </div>

      <p className="text-[10px] mono uppercase tracking-[0.22em] text-[var(--muted)] mt-3 text-center flex items-center justify-center gap-1.5">
        <Send className="w-3 h-3 text-[var(--neon)]" />
        envio para {STORE_WHATSAPP.replace(/^55/, "+55 ")}
      </p>
    </div>
  );
}
